import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFilePdf, FaSpinner } from 'react-icons/fa'
import { COMPANY, ESTIMATE_NOTES, BASE_EXCLUSIONS } from '../../../lib/estimateData'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const HEADER_SRC = '/header.png'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ── Helpers ───────────────────────────────────────────────────────────────────
const CAT_LABEL_COLOR = {
    CONCRETE: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    FENCE:    { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    PERGOLA:  { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
}

// ── B3b: Token-overlap suppression helpers ────────────────────────────────────
const STOP_WORDS = new Set([
    'city', 'from', 'with', 'required', 'upon', 'included', 'including',
    'and', 'the', 'for', 'not', 'that', 'this', 'are', 'will', 'some',
    'their', 'can', 'quoted', 'separately', 'needed',
    'approval', 'request', 'client', 'responsibility', 'installed', 'code',
    'depth', 'fees', 'plan', 'plans', 'if', 'by', 'or', 'to', 'of', 'in',
    'is', 'it', 'as', 'an', 'be', 'at', 'on', 'any', 'per', 'min',
])
const SYNONYMS = {
    sod: 'grass', turf: 'grass', lawn: 'grass',
    motorization: 'motor', motorized: 'motor',
    demolish: 'demolition', demo: 'demolition',
    permitting: 'permit', permits: 'permit',
    electrical: 'electric', electricity: 'electric',
    plumbing: 'plumb',
    install: 'installation', installing: 'installation',
    supply: 'supplies',
    decorative: 'decor', decoration: 'decor',
}

const tokenize = (str) => {
    return str
        .toLowerCase()
        .split(/[\s,;:()\-/]+/)
        .map(w => SYNONYMS[w] || w)
        .filter(w => w.length >= 4 && !STOP_WORDS.has(w))
}

const hasTokenOverlap = (excBullet, incBullet, threshold = 2) => {
    const excTokens = new Set(tokenize(excBullet))
    const incTokens = tokenize(incBullet)
    let common = 0
    for (const t of incTokens) {
        if (excTokens.has(t)) common++
        if (common >= threshold) return true
    }
    return false
}

// ── Proposal document (printable) ────────────────────────────────────────────
function ProposalDocument({ estimate, t, locale, contentId = 'estimate-content' }) {
    // Group lines by category (B4)
    const grouped = []
    const catOrder = []
    for (const line of estimate.lines) {
        const cat = line.cat || 'OTHER'
        if (!catOrder.includes(cat)) catOrder.push(cat)
    }
    for (const cat of catOrder) {
        grouped.push({ cat, lines: estimate.lines.filter(l => (l.cat || 'OTHER') === cat) })
    }
    const multipleCategories = grouped.length > 1
    const totalSale = estimate.lines.reduce((s, l) => s + (l.qty || 0) * (l.price || 0), 0)

    // Gather all scope bullets (deduplicated)
    const allInc = []
    const allExc = []
    const seenInc = new Set()
    const seenExc = new Set()
    for (const line of estimate.lines) {
        for (const b of (line.inc || [])) {
            if (!seenInc.has(b)) { seenInc.add(b); allInc.push(b) }
        }
        for (const b of (line.exc || [])) {
            if (!seenExc.has(b)) { seenExc.add(b); allExc.push(b) }
        }
    }

    // Category-aware BASE_EXCLUSIONS: filter concrete-specific bullets when no CONCRETE items are present
    const presentCats = new Set(estimate.lines.map(l => (l.cat || '').toUpperCase()))
    const CONCRETE_ONLY_BULLETS = new Set([
        'Drainage engineering plans (required by some cities \u2014 quoted separately if needed)',
        'Natural or artificial grass not included',
        'Natural grass installation in right-of-way and setback areas - required by city for final inspection approval; can be quoted separately upon request',
        'Decorative, stamped, or colored finishes',
    ])
    const filteredBaseExclusions = (estimate.lines || []).length > 0
        ? BASE_EXCLUSIONS.filter(b => {
            const explicitlyListed = allExc.some(exc => exc.toLowerCase() === b.toLowerCase())
            const concreteOk = !CONCRETE_ONLY_BULLETS.has(b) || presentCats.has('CONCRETE')
            return explicitlyListed && concreteOk
        })
        : []

    // B3 — Smart exclude suppression (two passes):
    // Pass A (substring): INC contains EXC text or vice-versa.
    // Pass 0: Build ID-based lookup maps for exact sub-service suppression.
    // allIncIds — set of every UUID in any line's inc_ids.
    // excTextToId — maps an exc bullet's text → its UUID (from the parallel exc/exc_ids arrays).
    // NOTE: BASE_EXCLUSIONS are global strings with no IDs — Pass 0 never fires for them.
    const allIncIds = new Set(estimate.lines.flatMap(l => l.inc_ids || []))
    const excTextToId = new Map()
    for (const line of estimate.lines) {
        const ids  = line.exc_ids || []
        const txts = line.exc     || []
        ids.forEach((id, i) => { if (txts[i]) excTextToId.set(txts[i], id) })
    }

    // Pass B (token overlap): ≥2 meaningful tokens in common after stop-word removal + synonym folding.
    //   → catches semantically equivalent bullets with different wording (e.g. "natural sod" ↔ "grass installation").
    //   → false-positive guard: single-token matches (e.g. just "electrical") are NOT suppressed.
    const isCovered = (excBullet) => {
        // Pass 0 — exact sub-service ID match (fastest, most accurate)
        const excId = excTextToId.get(excBullet)
        if (excId && allIncIds.has(excId)) return true

        const excLower = excBullet.toLowerCase().trim()
        return allInc.some(incBullet => {
            const incLower = incBullet.toLowerCase().trim()
            // Pass A — bidirectional substring
            if (incLower.includes(excLower) || excLower.includes(incLower)) return true
            // Pass B — token overlap (threshold = 2 meaningful tokens)
            return hasTokenOverlap(excBullet, incBullet)
        })
    }

    // Merge filtered BASE_EXCLUSIONS + item-specific EXC bullets, deduped.
    // Apply B3 suppression to BOTH tiers so that if an INC bullet truly covers a global
    // exclusion, it is still hidden (edge case, but correct).
    const mergedExc = [
        ...filteredBaseExclusions,
        ...allExc.filter(b => !filteredBaseExclusions.some(g => g.toLowerCase() === b.toLowerCase())),
    ]
    const filteredExc = mergedExc.filter(b => !isCovered(b))

    const FALLBACK_SCHEDULE = [
        { label: t('admin.estimator.payment_30_deposit'),  pct: 30 },
        { label: t('admin.estimator.payment_40_permit'),  pct: 40 },
        { label: t('admin.estimator.payment_20_completion'), pct: 20 },
        { label: t('admin.estimator.payment_10_inspection'), pct: 10 },
    ]
    const paymentSchedule = estimate.paymentSchedule?.length > 0
        ? estimate.paymentSchedule
        : FALLBACK_SCHEDULE
    const scheduleTotal = paymentSchedule.reduce((s, r) => s + (Number(r.pct) || 0), 0)
    const scheduleValid = scheduleTotal === 100

    let globalIdx = 0

    return (
        <div
            id="estimate-document"
            style={{
                fontFamily: 'Arial, Helvetica, sans-serif',
                color: '#1a1a1a',
                background: '#fff',
                width: '100%',
                maxWidth: 'none',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
            }}
        >
            {/* ── Header image strip ── */}
            <img src={HEADER_SRC} alt="" data-role="print-header" style={{ width: '100%', display: 'block', flexShrink: 0 }} />

            {/* ── Content area ── */}
            <div id={contentId} className="px-4 sm:px-14" style={{
                paddingTop: 20,
                paddingBottom: 20,
                maxWidth: '760px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box',
                flex: 1,
            }}>


            {/* ── Meta info ── */}
            <table className="text-[10px] sm:text-xs" style={{ width: '100%', marginBottom: 28, borderCollapse: 'collapse' }}>
                <tbody>
                    {[
                        [t('admin.estimator.preview_estimate_no'), estimate.number],
                        [t('admin.estimator.preview_date'), estimate.date
                            ? (() => { const d = new Date(estimate.date + 'T12:00:00'); return isNaN(d) ? estimate.date : d.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' }) })()
                            : '—'],
                        [t('admin.estimator.client_name'), estimate.client || '—'],
                        [t('admin.estimator.client_phone'), estimate.addressDetails?.clientPhone || '—'],
                        [t('admin.estimator.client_email'), estimate.addressDetails?.clientEmail || '—'],
                        [t('admin.estimator.project_address'), estimate.addressDetails?.address || estimate.address || '—'],
                        [t('admin.estimator.preview_contractor'), `${COMPANY.name}  (${t('admin.estimator.preview_license')} ${COMPANY.license})`],
                        [t('admin.estimator.valid_for'), t('admin.estimator.preview_valid_for_days', { days: estimate.validDays || 30 })],
                    ].map(([label, value]) => (
                        <tr key={label} style={{ borderBottom: '1px solid #e5e5e5', pageBreakInside: 'avoid' }}>
                            <td className="py-1 pr-2 w-24 sm:w-36 sm:pr-3" style={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', verticalAlign: 'top' }}>{label}</td>
                            <td className="py-1" style={{ color: '#1a1a1a', fontWeight: label === t('admin.estimator.client_name') || label === t('admin.estimator.project_address') ? 600 : 400 }}>{value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ── Scope of Work heading ── */}
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a', marginBottom: 10, borderBottom: '2px solid #1a1a1a', paddingBottom: 4, pageBreakAfter: 'avoid' }}>
                {t('admin.estimator.preview_scope_of_work')}
            </div>

            {/* ── Subtitle ── */}
            {estimate.subtitle && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {estimate.subtitle}
                </div>
            )}

            {/* ── Services table — grouped by category (B4) ── */}
            <table className="w-full" style={{ borderCollapse: 'collapse', marginBottom: 0 }}>
                <thead>
                    <tr style={{ background: '#1a1a1a', color: '#fff' }}>
                        <th className="w-5 sm:w-8 text-center whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</th>
                        <th className="text-left whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admin.estimator.col_description')}</th>
                        <th className="text-center whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admin.estimator.col_unit')}</th>
                        <th className="text-center whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admin.estimator.col_qty')}</th>
                        <th className="text-right whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admin.estimator.col_unit_price')}</th>
                        <th className="text-right whitespace-nowrap py-1.5 px-1 sm:px-2.5 text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admin.estimator.col_total')}</th>
                    </tr>
                </thead>
                <tbody>
                    {estimate.lines.length === 0 ? (
                        <tr><td colSpan={6} className="py-4 text-xs text-center sm:text-sm" style={{ color: '#999', fontStyle: 'italic' }}>{t('admin.estimator.preview_no_items_added')}</td></tr>
                    ) : grouped.map(({ cat, lines: catLines }) => {
                        const catTotal = catLines.reduce((s, l) => s + (l.qty || 0) * (l.price || 0), 0)
                        const catColors = CAT_LABEL_COLOR[cat] || { bg: '#f5f5f5', text: '#555', border: '#ddd' }
                        return (
                            <React.Fragment key={cat}>
                                {multipleCategories && (
                                    <tr style={{ pageBreakInside: 'avoid' }}>
                                        <td colSpan={6} className="py-1 px-1.5 sm:px-2.5" style={{ background: catColors.bg, borderBottom: `1px solid ${catColors.border}` }}>
                                            <span className="text-[9px] sm:text-[10px]" style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: catColors.text }}>{cat}</span>
                                        </td>
                                    </tr>
                                )}
                                {catLines.map((line) => {
                                    const idx = globalIdx++
                                    const lineTotal = (line.qty || 0) * (line.price || 0)
                                    return (
                                        <tr key={idx} style={{ background: idx % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #eee', pageBreakInside: 'avoid' }}>
                                            <td className="text-[10px] sm:text-[11px] text-center py-1 px-1 sm:px-2.5" style={{ color: '#888' }}>{idx + 1}</td>
                                            <td className="text-[10px] sm:text-[11px] min-w-0 py-1 px-1 sm:px-2.5 break-words" style={{ color: '#1a1a1a', lineHeight: 1.4 }}>
                                            {line.name}
                                            {line.subcat && <div className="text-[9px] sm:text-[10px]" style={{ color: '#888', fontStyle: 'italic', marginTop: 2 }}>{line.subcat}</div>}
                                        </td>
                                            <td className="text-[10px] sm:text-[11px] text-center whitespace-nowrap py-1 px-1 sm:px-2.5" style={{ color: '#666' }}>{line.unit}</td>
                                            <td className="text-[10px] sm:text-[11px] text-center py-1 px-1 sm:px-2.5 font-semibold" style={{ color: '#1a1a1a' }}>{line.qty}</td>
                                            <td className="text-[10px] sm:text-[11px] text-right py-1 px-1 sm:px-2.5" style={{ color: '#1a1a1a' }}>${fmt(line.price)}</td>
                                            <td className="text-[11px] sm:text-xs text-right py-1 px-1 sm:px-2.5 font-bold" style={{ color: '#1a1a1a' }}>${fmt(lineTotal)}</td>
                                        </tr>
                                    )
                                })}
                                {multipleCategories && (
                                    <tr style={{ background: catColors.bg, borderBottom: `2px solid ${catColors.border}`, pageBreakInside: 'avoid' }}>
                                        <td colSpan={5} className="text-[9px] sm:text-[10px] py-1 px-1 sm:px-2.5 text-right" style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: catColors.text }}>
                                            {t('admin.estimator.preview_subtotal')} — {cat}
                                        </td>
                                        <td className="text-[11px] sm:text-sm py-1 px-1 sm:px-2.5 text-right font-bold" style={{ color: catColors.text }}>${fmt(catTotal)}</td>
                                    </tr>
                                )}
                            </React.Fragment>
                        )
                    })}
                </tbody>
            </table>

            {/* ── Project Total ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24, marginTop: 0 }}>
                <div className="py-1.5 px-3 sm:py-2 sm:px-5 gap-2 sm:gap-3.5" style={{ background: '#1a1a1a', color: '#fff', borderRadius: '0 0 5px 5px', display: 'flex', alignItems: 'center' }}>
                    <span className="text-[9px] sm:text-[10px]" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>{t('admin.estimator.preview_project_total')}</span>
                    <span className="text-xs sm:text-sm" style={{ fontWeight: 900 }}>${fmt(totalSale)}</span>
                </div>
            </div>

            {/* ── Scope Detail ── */}
            {(allInc.length > 0 || filteredExc.length > 0) && (
                <>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a', marginBottom: 12, borderBottom: '2px solid #1a1a1a', paddingBottom: 4, pageBreakAfter: 'avoid' }}>
                        {t('admin.estimator.preview_scope_detail')}
                    </div>
                    <div className="grid grid-cols-1 gap-6 mb-7 sm:grid-cols-2">
                        {allInc.length > 0 && (
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                                    ✓ {t('admin.estimator.preview_included_in_estimate')}
                                </div>
                                {allInc.map((b, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start', pageBreakInside: 'avoid' }}>
                                        <span style={{ color: '#16a34a', marginTop: 2, fontSize: 11 }}>✔</span>
                                        <span style={{ fontSize: 11, color: '#333', lineHeight: 1.4 }}>{b}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {filteredExc.length > 0 && (
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                                    ✕ {t('admin.estimator.not_included')}
                                </div>
                                {filteredExc.map((b, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start', pageBreakInside: 'avoid' }}>
                                        <span style={{ color: '#dc2626', marginTop: 2, fontSize: 11 }}>•</span>
                                        <span style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>{b}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── Important Notes ── */}
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a', marginBottom: 8, borderBottom: '2px solid #1a1a1a', paddingBottom: 4, pageBreakAfter: 'avoid' }}>
                {t('admin.estimator.preview_important_notes')}
            </div>
            <div style={{ marginBottom: 24 }}>
                {ESTIMATE_NOTES.map((note, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5, pageBreakInside: 'avoid' }}>
                        <span style={{ color: '#1a1a1a', fontWeight: 700 }}>•</span>
                        <span style={{ fontSize: 11, color: '#333', lineHeight: 1.5 }}>{note}</span>
                    </div>
                ))}
            </div>

            {/* ── Additional Notes (B2) ── */}
            {estimate.notes && estimate.notes.trim() && (
                <>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a', marginBottom: 8, borderBottom: '2px solid #1a1a1a', paddingBottom: 4, pageBreakAfter: 'avoid' }}>
                        {t('admin.estimator.preview_additional_agreements')}
                    </div>
                    <div style={{ marginBottom: 24, padding: '12px 14px', background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 6 }}>
                        <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{estimate.notes}</p>
                    </div>
                </>
            )}

            {/* ── Payment Schedule ── */}
            {totalSale > 0 && (
                <>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a', marginBottom: 10, borderBottom: '2px solid #1a1a1a', paddingBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pageBreakAfter: 'avoid' }}>
                        <span>{t('admin.estimator.payment_schedule')}</span>
                        {!scheduleValid && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', textTransform: 'none', letterSpacing: 0 }}>{t('admin.estimator.preview_schedule_incomplete', { pct: scheduleTotal })}</span>
                        )}
                    </div>
                    <table className="text-[10px] sm:text-[11px]" style={{ width: '100%', marginBottom: 28, borderCollapse: 'collapse' }}>
                        <tbody>
                            {paymentSchedule.map((row, i) => {
                                const amount = totalSale * ((Number(row.pct) || 0) / 100)
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid #eee', pageBreakInside: 'avoid' }}>
                                        <td className="py-1 pr-3 sm:pr-6" style={{ color: '#333' }}>{row.label || t('admin.estimator.preview_payment_n', { n: i + 1 })}</td>
                                        <td className="py-1 font-bold text-right whitespace-nowrap" style={{ color: '#1a1a1a' }}>${fmt(amount)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {/* ── Closing ── */}
            <div style={{ pageBreakInside: 'avoid' }}>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 16, lineHeight: 1.6, fontStyle: 'italic' }}>
                    {t('admin.estimator.preview_closing_text')}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', marginBottom: 32 }}>
                    {t('admin.estimator.preview_signature')}
                </div>
            </div>

            {/* ── Signature block ── */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-10" style={{ borderTop: '2px solid #1a1a1a', paddingTop: 20, marginBottom: 28, pageBreakInside: 'avoid' }}>
                {[[t('admin.estimator.preview_client_signature'), t('admin.estimator.preview_print_name'), t('admin.estimator.preview_signature_date')], [t('admin.estimator.preview_contractor_signature'), t('admin.estimator.preview_signature_title'), t('admin.estimator.preview_signature_date')]].map((fields, col) => (
                    <div key={col}>
                        {fields.map((field) => (
                            <div key={field} style={{ marginBottom: 16 }}>
                                <div className="text-[9px] sm:text-[10px]" style={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{field}</div>
                                <div style={{ borderBottom: '1px solid #bbb', height: 24 }} />
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* ── Company contact footer (D) ── */}
            <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <p style={{ fontSize: 10, fontWeight: 800, color: '#1a1a1a', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>{COMPANY.name}</p>
                    <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>{t('admin.estimator.preview_license')} {COMPANY.license}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 10, color: '#888', margin: 0 }}>{COMPANY.phone} · {COMPANY.email}</p>
                    <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>{COMPANY.website}</p>
                </div>
            </div>

            </div>{/* end content area */}

            {/* ── Footer image strip ── */}
            <img src="/footer.png" alt="" data-role="print-footer" style={{ width: '100%', display: 'block', flexShrink: 0 }} />
        </div>
    )
}

// ── Main preview wrapper ──────────────────────────────────────────────────────
export default function EstimatePreview({ estimate }) {
    const { t, i18n } = useTranslation()
    const [exporting, setExporting] = useState(false)
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US'
    const enT = i18n.getFixedT('en')

    const buildPDF = async () => {
        const el = document.getElementById('estimate-content-pdf')
        if (!el) throw new Error('estimate-content-pdf not found')
        const pdf   = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' })
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const loadImg = (src) => new Promise((res, rej) => {
            const img = new Image(); img.crossOrigin = 'anonymous'
            img.onload = () => {
                const c = document.createElement('canvas')
                c.width = img.naturalWidth; c.height = img.naturalHeight
                c.getContext('2d').drawImage(img, 0, 0)
                res({ data: c.toDataURL('image/png'), aspect: img.naturalHeight / img.naturalWidth })
            }
            img.onerror = rej; img.src = src
        })
        const [hdr, ftr] = await Promise.all([
            loadImg(`${window.location.origin}/header.png`),
            loadImg(`${window.location.origin}/footer.png`),
        ])
        const hdrH  = hdr.aspect * pageW
        const ftrH  = ftr.aspect * pageW
        const slotH = pageH - hdrH - ftrH
        const SCALE = 2, ELEM_W = 760
        const canvas = await html2canvas(el, {
            scale: SCALE, useCORS: true, allowTaint: true,
            backgroundColor: '#ffffff', windowWidth: ELEM_W,
        })
        const ptPerPx = pageW / (ELEM_W * SCALE)
        const slotPx  = Math.floor(slotH / ptPerPx)
        const findBreak = (tgt) => {
            const ctx  = canvas.getContext('2d')
            const back = Math.floor(slotPx * 0.08)
            for (let y = tgt; y > tgt - back; y--) {
                const d = ctx.getImageData(0, y, canvas.width, 1).data
                let dark = 0
                for (let i = 0; i < d.length; i += 4)
                    if (d[i] < 240 || d[i + 1] < 240 || d[i + 2] < 240) dark++
                if (dark < canvas.width * 0.03) return y
            }
            return tgt
        }
        let srcY = 0, first = true
        while (srcY < canvas.height) {
            if (!first) pdf.addPage()
            const rawEnd  = srcY + slotPx
            const safeEnd = rawEnd >= canvas.height ? canvas.height : findBreak(rawEnd)
            const h = safeEnd - srcY
            const slice = document.createElement('canvas')
            slice.width = canvas.width; slice.height = h
            const ctx = slice.getContext('2d')
            ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, slice.width, slice.height)
            ctx.drawImage(canvas, 0, srcY, canvas.width, h, 0, 0, canvas.width, h)
            pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', 0, hdrH, pageW, h * ptPerPx)
            pdf.addImage(hdr.data, 'PNG', 0,            0, pageW, hdrH, '', 'FAST')
            pdf.addImage(ftr.data, 'PNG', 0, pageH - ftrH, pageW, ftrH, '', 'FAST')
            srcY = safeEnd; first = false
        }
        return pdf
    }

    const handleExportPDF = async () => {
        setExporting(true)
        try {
            const pdf = await buildPDF()
            pdf.save(`Estimate_${estimate.number}_${(estimate.client || t('admin.estimator.client_placeholder')).replace(/\s+/g, '-')}.pdf`)
        } catch (err) {
            console.error('PDF export error:', err)
            alert(t('admin.estimator.preview_export_error'))
        } finally {
            setExporting(false)
        }
    }

    return (
        <div>
            {/* Action bar */}
            <div className="flex flex-wrap gap-2 items-center mb-4">
                <div className="flex gap-2 items-center ml-auto">
                    <button
                        onClick={handleExportPDF}
                        disabled={exporting}
                        className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-60"
                    >
                        {exporting ? <FaSpinner size={13} className="animate-spin" /> : <FaFilePdf size={13} />}
                        {exporting ? t('admin.estimator.preview_exporting') : t('admin.estimator.preview_export_pdf')}
                    </button>
                </div>
            </div>

            {/* Document preview */}
            <div className="rounded-xl border border-gray-200 shadow-sm" style={{ background: '#f0ede8' }}>
                {estimate.lines.length === 0 && (
                    <div className="flex gap-2 items-center px-6 py-3 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
                        <FaFilePdf size={12} />
                        {t('admin.estimator.preview_empty_builder_notice')}
                    </div>
                )}
                <div className="overflow-x-auto min-w-0">
                    <ProposalDocument estimate={estimate} t={t} locale={locale} />
                </div>
            </div>

            {/* Hidden English-only document for PDF export */}
            <div className="hidden-pdf-source" style={{ position: 'absolute', left: '-9999px', top: 0, width: 760, zIndex: -1 }}>
                <ProposalDocument estimate={estimate} t={enT} locale="en-US" contentId="estimate-content-pdf" />
            </div>
        </div>
    )
}
